import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import { FilePlus, ArrowRight, Clock, CheckCircle, Package, Sparkle } from "@phosphor-icons/react";

const STATUS_COLORS = {
  draft: "bg-[var(--pf-surface-2)] text-[var(--pf-text-2)]",
  designing: "bg-[var(--pf-yellow)] text-black",
  awaiting_designer: "bg-[var(--pf-yellow)] text-black",
  template_pending: "bg-[var(--pf-cyan)] text-black",
  review: "bg-[var(--pf-cyan)] text-black",
  approved: "bg-[var(--pf-brand)] text-white",
  ordered: "bg-[var(--pf-text)] text-white",
};

const STATUS_LABELS = {
  draft: "draft",
  designing: "designer working",
  awaiting_designer: "awaiting designer",
  template_pending: "template being prepared",
  review: "ready to review",
  approved: "approved",
  ordered: "ordered",
};

export default function ClientDashboard() {
  const [briefs, setBriefs] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/briefs"), api.get("/orders")])
      .then(([b, o]) => { setBriefs(b.data); setOrders(o.data); })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[var(--pf-bg)]">
      <Nav />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-12 md:py-16">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="grid grid-cols-1 md:grid-cols-12 items-end gap-4 mb-12">
          <div className="md:col-span-8">
            <div className="pf-label mb-3">Your workspace</div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]">Print, forged by AI.</h1>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/brief/new" className="pf-btn-primary pf-stamped" data-testid="dashboard-new-brief">
              <FilePlus size={16} weight="bold" /> New Brief
            </Link>
          </div>
        </motion.div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {[
            { l:"Briefs", v:briefs.length, i:Sparkle, tint:"var(--pf-yellow)" },
            { l:"In review", v:briefs.filter(b=>b.status==="review").length, i:Clock, tint:"var(--pf-cyan)" },
            { l:"Orders", v:orders.length, i:Package, tint:"var(--pf-brand)" },
            { l:"Delivered", v:orders.filter(o=>o.status==="delivered").length, i:CheckCircle, tint:"#22C55E" },
          ].map((s,i) => (
            <motion.div key={s.l} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i*0.06, duration: 0.4 }}
              className="border border-[var(--pf-border)] bg-white p-5 hover:border-[var(--pf-text)] transition-colors" data-testid={`stat-${s.l.toLowerCase()}`}>
              <div className="flex items-start justify-between">
                <div className="pf-label">{s.l}</div>
                <div className="w-8 h-8 flex items-center justify-center" style={{ backgroundColor: s.tint }}><s.i size={16} weight="duotone" className="text-black" /></div>
              </div>
              <div className="font-mono text-4xl font-black mt-4 tracking-tighter">{s.v}</div>
            </motion.div>
          ))}
        </div>

        {/* Briefs */}
        <section className="mb-16">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="pf-label mb-2">Section · Briefs</div>
              <h2 className="text-2xl md:text-3xl font-black tracking-tight">Your print briefs</h2>
            </div>
          </div>
          {loading ? <div className="pf-label">Loading…</div> : briefs.length === 0 ? (
            <div className="border border-dashed border-[var(--pf-border)] p-16 text-center bg-white" data-testid="empty-briefs">
              <Sparkle size={32} className="mx-auto mb-4 text-[var(--pf-text-3)]" />
              <p className="text-[var(--pf-text-2)] mb-5">No briefs yet. Get started with your first one.</p>
              <Link to="/brief/new" className="pf-btn-outline">Submit a brief</Link>
            </div>
          ) : (
            <div className="border border-[var(--pf-border)] bg-white">
              {briefs.map((b) => (
                <Link key={b.id} to={`/brief/${b.id}`} data-testid={`brief-row-${b.id}`}
                  className="grid grid-cols-12 gap-4 items-center p-5 border-b last:border-0 border-[var(--pf-border)] hover:bg-[var(--pf-bg)] transition-colors">
                  <div className="col-span-12 md:col-span-5">
                    <div className="font-mono text-xs text-[var(--pf-text-3)]">#{b.id.slice(0,8).toUpperCase()}</div>
                    <div className="font-bold text-lg mt-1">{b.title}</div>
                  </div>
                  <div className="col-span-6 md:col-span-3 pf-label">{b.product_type.replace('_',' ')}</div>
                  <div className="col-span-3 md:col-span-2 font-mono text-sm">{b.quantity.toLocaleString()} qty</div>
                  <div className="col-span-3 md:col-span-2 text-right">
                    <span className={`inline-block text-[10px] tracking-widest uppercase font-bold px-2 py-1 ${STATUS_COLORS[b.status]||"bg-[var(--pf-surface-2)]"}`}>{STATUS_LABELS[b.status] || b.status}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* Orders shortcut */}
        <section>
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="pf-label mb-2">Section · Orders</div>
              <h2 className="text-2xl md:text-3xl font-black tracking-tight">Recent orders</h2>
            </div>
            <Link to="/orders" className="pf-label hover:text-[var(--pf-brand)]">View all →</Link>
          </div>
          {orders.length === 0 ? (
            <div className="border border-dashed border-[var(--pf-border)] p-10 text-center text-[var(--pf-text-2)] bg-white" data-testid="empty-orders">
              No orders yet.
            </div>
          ) : (
            <div className="border border-[var(--pf-border)] bg-white">
              {orders.slice(0,5).map((o) => (
                <Link key={o.id} to={`/orders/${o.id}`} className="grid grid-cols-12 gap-4 items-center p-5 border-b last:border-0 border-[var(--pf-border)] hover:bg-[var(--pf-bg)] transition-colors">
                  <div className="col-span-6 md:col-span-5">
                    <div className="font-mono text-xs text-[var(--pf-text-3)]">#{o.id.slice(0,8).toUpperCase()}</div>
                    <div className="font-bold mt-1">{o.product_type.replace('_',' ')} × {o.quantity}</div>
                  </div>
                  <div className="col-span-3 font-mono text-sm">₹{o.total_amount.toLocaleString()}</div>
                  <div className="col-span-3 md:col-span-3 text-right">
                    <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 bg-[var(--pf-surface-2)]">{o.status.replace('_',' ')}</span>
                  </div>
                  <div className="hidden md:block md:col-span-1 text-right"><ArrowRight size={16}/></div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
